import React, { useState, useRef, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const SoftwareLicenceMultiSelect = ({
  licences = [],
  selectedIds = [],
  onChange,
  label = 'Software & Licences',
  helperText = '(Optional - Select multiple enterprise software)',
  placeholder = 'Select software & licences...',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  // Normalize selected IDs as strings for easy comparison
  const normalizedSelectedIds = useMemo(() => {
    if (!Array.isArray(selectedIds)) {
      return selectedIds ? [String(selectedIds)] : [];
    }
    return selectedIds.map((id) => String(id));
  }, [selectedIds]);

  // Selected licence objects
  const selectedLicences = useMemo(() => {
    return licences.filter((lic) => normalizedSelectedIds.includes(String(lic.id)));
  }, [licences, normalizedSelectedIds]);

  // Filtered licences based on search query
  const filteredLicences = useMemo(() => {
    if (!searchQuery.trim()) return licences;
    const q = searchQuery.toLowerCase();
    return licences.filter((lic) => {
      const name = (lic.software || '').toLowerCase();
      const vendor = (lic.vendor_relation?.name || lic.vendor || '').toLowerCase();
      const type = (lic.licence_type || lic.licenceType || '').toLowerCase();
      const version = (lic.version || '').toLowerCase();
      return name.includes(q) || vendor.includes(q) || type.includes(q) || version.includes(q);
    });
  }, [licences, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (id) => {
    const strId = String(id);
    let updated;
    if (normalizedSelectedIds.includes(strId)) {
      updated = normalizedSelectedIds.filter((item) => item !== strId);
    } else {
      updated = [...normalizedSelectedIds, strId];
    }
    onChange(updated);
  };

  const handleRemove = (id, e) => {
    e.stopPropagation();
    const strId = String(id);
    const updated = normalizedSelectedIds.filter((item) => item !== strId);
    onChange(updated);
  };

  const handleSelectAll = () => {
    const filteredIds = filteredLicences.map((l) => String(l.id));
    const merged = Array.from(new Set([...normalizedSelectedIds, ...filteredIds]));
    onChange(merged);
  };

  const handleClearAll = (e) => {
    if (e) e.stopPropagation();
    onChange([]);
  };

  return (
    <div className={`w-full relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium mb-1.5 text-foreground flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <Icon name="Layers" size={15} className="text-primary" />
            <span>{label}</span>
            {helperText && (
              <span className="text-muted-foreground text-xs font-normal">
                {helperText}
              </span>
            )}
          </span>
          {normalizedSelectedIds.length > 0 && (
            <span className="text-xs font-semibold text-primary px-2 py-0.5 rounded-full bg-primary/10">
              {normalizedSelectedIds.length} Selected
            </span>
          )}
        </label>
      )}

      {/* Main Trigger Bar */}
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className={`min-h-[42px] w-full border rounded-lg p-2 cursor-pointer flex flex-wrap items-center gap-1.5 bg-background dark:bg-gray-800 transition-colors ${
          isOpen ? 'ring-2 ring-blue-500 border-blue-500' : 'border-border hover:border-gray-400'
        }`}
      >
        {selectedLicences.length === 0 ? (
          <span className="text-muted-foreground text-sm flex items-center gap-2 select-none px-1">
            <Icon name="PackagePlus" size={16} className="text-muted-foreground/60" />
            {placeholder}
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5 flex-1">
            {selectedLicences.map((lic) => (
              <span
                key={lic.id}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shadow-sm animate-in fade-in"
              >
                <Icon name="ShieldCheck" size={13} className="text-blue-600 dark:text-blue-400" />
                <span className="font-semibold">{lic.software}</span>
                {lic.version && (
                  <span className="text-[10px] opacity-80">v{lic.version}</span>
                )}
                {(lic.licence_type || lic.licenceType) && (
                  <span className="text-[10px] px-1 py-0.2 bg-blue-100 dark:bg-blue-900 rounded text-blue-800 dark:text-blue-200">
                    {lic.licence_type || lic.licenceType}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => handleRemove(lic.id, e)}
                  className="ml-1 hover:bg-blue-200 dark:hover:bg-blue-800 rounded p-0.5 transition-colors"
                  title="Remove software"
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="ml-auto flex items-center gap-1 text-muted-foreground pl-2">
          {normalizedSelectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs text-muted-foreground hover:text-red-500 p-1 hover:bg-muted rounded"
              title="Clear all selected"
            >
              <Icon name="X" size={14} />
            </button>
          )}
          <Icon
            name={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            className="transition-transform duration-200"
          />
        </div>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-card dark:bg-gray-800 border border-border rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Search & Actions Bar */}
          <div className="p-2 border-b border-border bg-muted/30 space-y-2">
            <div className="relative">
              <Icon
                name="Search"
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search software, vendor, version..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-background dark:bg-gray-900 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={13} />
                </button>
              )}
            </div>

            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-muted-foreground">
                Showing {filteredLicences.length} of {licences.length} licences
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Select All
                </button>
                <span className="text-muted-foreground">•</span>
                <button
                  type="button"
                  onClick={() => handleClearAll()}
                  className="text-muted-foreground hover:text-red-500 font-medium"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Licences List */}
          <div className="max-h-60 overflow-y-auto divide-y divide-border/50">
            {filteredLicences.length === 0 ? (
              <div className="p-4 text-center text-xs text-muted-foreground">
                <Icon name="Layers" size={20} className="mx-auto mb-1 opacity-40" />
                No matching software licences found.
              </div>
            ) : (
              filteredLicences.map((lic) => {
                const isSelected = normalizedSelectedIds.includes(String(lic.id));
                const vendorName = lic.vendor_relation?.name || lic.vendor || '';
                const licenceType = lic.licence_type || lic.licenceType || 'Subscription';
                const version = lic.version || '';

                return (
                  <div
                    key={lic.id}
                    onClick={() => handleToggle(lic.id)}
                    className={`flex items-start gap-2.5 p-2.5 cursor-pointer text-xs transition-colors ${
                      isSelected
                        ? 'bg-blue-50/70 dark:bg-blue-950/40 text-blue-950 dark:text-blue-100 font-medium'
                        : 'hover:bg-muted/50 text-foreground'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Controlled by row click
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm leading-tight text-foreground">
                          {lic.software}
                        </span>
                        {version && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground font-mono">
                            v{version}
                          </span>
                        )}
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300">
                          {licenceType}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                        {vendorName && (
                          <span className="flex items-center gap-1 truncate">
                            <Icon name="Building2" size={11} className="opacity-70" />
                            {vendorName}
                          </span>
                        )}
                        {lic.licence_key && (
                          <span className="font-mono text-[10px] text-muted-foreground/80 truncate">
                            Key: {lic.licence_key.slice(0, 14)}...
                          </span>
                        )}
                        {lic.cost > 0 && (
                          <span className="text-emerald-600 dark:text-emerald-400 font-medium ml-auto">
                            ${Number(lic.cost).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Done Bar */}
          <div className="p-2 border-t border-border bg-muted/20 flex justify-end">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-semibold rounded-md shadow-sm transition-colors"
            >
              Done ({normalizedSelectedIds.length})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoftwareLicenceMultiSelect;

